import { useCallback, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTE_IDS, type RouteAccessDecision } from '@finance/contracts';
import { useSession } from '../../features/auth/session-provider';
import { useLogout } from '../../features/auth/useLogout';
import { getRouteById, getRouteByPath } from '../../routes/registry';
import { buildAccountMenuViewModel } from './account-menu-model';
import { useAccountShell } from './use-account-shell';
import { apiFetch, refreshAccessToken, setAccessToken } from '../../api/http-client';

export interface UseAccountMenuResult {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  triggerId: string;
  menuId: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  viewModel: ReturnType<typeof buildAccountMenuViewModel>;
  contextSwitchState: string;
  logoutPending: boolean;
  delegationExitPending: boolean;
  navigationLoading: boolean;
  navigationError: boolean;
  refetchNavigation: () => Promise<unknown>;
  handleOpen: (event: MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  handleLogout: () => Promise<void>;
  handleExitDelegation: () => Promise<void>;
  handleSwitchContext: (membershipId: string, branchId?: string) => Promise<void>;
}

function routeActionable(availability: string | undefined): boolean {
  return availability === 'AVAILABLE' || availability === 'READ_ONLY';
}

export function useAccountMenu(): UseAccountMenuResult {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    switchContext,
    exitDelegation,
    logout: logoutLocal,
    refreshProfile,
    contextSwitchState,
    contextSwitchError,
  } = useSession();
  const logout = useLogout();
  const accountShell = useAccountShell();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [delegationExitPending, setDelegationExitPending] = useState(false);
  const [switchErrorOverride, setSwitchErrorOverride] = useState<string | null | undefined>(undefined);
  const [switchStateOverride, setSwitchStateOverride] = useState<string | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const effectiveSwitchError = switchErrorOverride === undefined ? contextSwitchError : switchErrorOverride;
  const effectiveSwitchState = switchStateOverride ?? contextSwitchState;

  const viewModel = useMemo(
    () => buildAccountMenuViewModel(accountShell.data, user, t, {
      switchPending: effectiveSwitchState === 'pending',
      logoutPending: logout.isPending,
      switchError: effectiveSwitchError,
    }),
    [accountShell.data, effectiveSwitchError, effectiveSwitchState, logout.isPending, t, user],
  );

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const handleLogout = useCallback(async () => {
    const loginPath = getRouteById(ROUTE_IDS.LOGIN)?.path ?? '/login';
    let remoteRevocationFailed = false;
    try {
      const result = await logout.mutateAsync();
      remoteRevocationFailed = !result.remoteRevocationSucceeded;
    } catch {
      remoteRevocationFailed = true;
    }
    setAnchorEl(null);
    const params = remoteRevocationFailed ? { signOutState: 'local-only' } : undefined;
    navigate(loginPath, { replace: true, ...(params ? { state: params } : {}) });
  }, [logout, navigate]);

  const reconcileRouteAfterContextChange = useCallback(async () => {
    const shellResult = await accountShell.refetch();
    const landingPath = shellResult.data?.landing.path ?? getRouteById(ROUTE_IDS.DASHBOARD)?.path ?? '/dashboard';
    const currentRoute = getRouteByPath(location.pathname);
    if (!currentRoute) {
      navigate(landingPath, { replace: true });
      return shellResult.data;
    }

    try {
      const decisions = await apiFetch<RouteAccessDecision[]>('/api/navigation/routes');
      const currentDecision = decisions.find((decision) => decision.routeId === currentRoute.routeId);
      if (!routeActionable(currentDecision?.availability)) {
        navigate(landingPath, { replace: true });
      }
    } catch {
      // Authorization reconciliation is security-sensitive. If current-route
      // validity cannot be confirmed after a context switch, use the server-
      // resolved landing route instead of leaving stale scoped content visible.
      navigate(landingPath, { replace: true });
    }
    return shellResult.data;
  }, [accountShell, location.pathname, navigate]);

  const reconcileAmbiguousSwitch = useCallback(async (membershipId: string, branchId: string) => {
    try {
      const accessToken = await refreshAccessToken();
      if (!accessToken) throw new Error('AUTH_CONTEXT_SWITCH_RECONCILIATION_FAILED');
      setAccessToken(accessToken);
      await refreshProfile();
      const shell = await reconcileRouteAfterContextChange();
      const selected = shell?.contextSwitch.options.some(
        (candidate) => candidate.selected && candidate.membershipId === membershipId && candidate.branchId === branchId,
      );
      if (selected) {
        setSwitchErrorOverride(null);
        setSwitchStateOverride('success');
        setAnchorEl(null);
        return true;
      }
      setSwitchErrorOverride(t('shell:userMenu.switchContextFailed'));
      setSwitchStateOverride('error');
      return false;
    } catch {
      // If neither the requested nor predecessor session can be reconciled,
      // clear local protected state. A stale access token must never be restored
      // after an atomic server-side context rotation.
      await logoutLocal();
      setAnchorEl(null);
      navigate(getRouteById(ROUTE_IDS.LOGIN)?.path ?? '/login', { replace: true });
      return false;
    }
  }, [logoutLocal, navigate, reconcileRouteAfterContextChange, refreshProfile, t]);

  const handleSwitchContext = useCallback(
    async (membershipId: string, branchId?: string) => {
      if (!branchId) return;
      const allowed = viewModel.contexts.some(
        (candidate) => candidate.membershipId === membershipId && candidate.branchId === branchId,
      );
      if (!allowed) return;
      setSwitchErrorOverride(undefined);
      setSwitchStateOverride('pending');
      try {
        await switchContext(membershipId, branchId);
        await reconcileRouteAfterContextChange();
        setSwitchErrorOverride(null);
        setSwitchStateOverride('success');
        setAnchorEl(null);
      } catch {
        await reconcileAmbiguousSwitch(membershipId, branchId);
      }
    },
    [reconcileAmbiguousSwitch, reconcileRouteAfterContextChange, switchContext, viewModel.contexts],
  );

  const handleExitDelegation = useCallback(async () => {
    if (!exitDelegation || !viewModel.canExitDelegation || delegationExitPending) return;
    setDelegationExitPending(true);
    try {
      await exitDelegation();
      const shellResult = await accountShell.refetch();
      const landingPath = shellResult.data?.landing.path ?? getRouteById(ROUTE_IDS.DASHBOARD)?.path ?? '/dashboard';
      setAnchorEl(null);
      navigate(landingPath, { replace: true });
    } finally {
      setDelegationExitPending(false);
    }
  }, [accountShell, delegationExitPending, exitDelegation, navigate, viewModel.canExitDelegation]);

  return {
    triggerRef,
    triggerId: 'account-menu-trigger',
    menuId: 'account-menu',
    anchorEl,
    open: Boolean(anchorEl),
    viewModel,
    contextSwitchState: effectiveSwitchState,
    logoutPending: logout.isPending,
    delegationExitPending,
    navigationLoading: accountShell.isLoading,
    navigationError: accountShell.isError,
    refetchNavigation: () => accountShell.refetch(),
    handleOpen,
    handleClose,
    handleLogout,
    handleExitDelegation,
    handleSwitchContext,
  };
}
